import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Link as MuiLink,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import { ROUTES } from '../constants/routes';

/**
 * Login page
 */
const Login = ({ themeMode, toggleTheme }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const getFriendlyErrorMessage = (errorCode) => {
    const key = `error.${errorCode?.replace('auth/', '')}`;
    const translated = t(key);
    return translated !== key ? translated : t('error.generic');
  };

  const handleLanguageChange = (event) => {
    const lang = event.target.value;
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      await authService.signIn(email, password);
      navigate(ROUTES.HOME);
    } catch (err) {
      // If error has a message property (from authService), use it
      // Otherwise try to translate the error code
      if (err.message && !err.message.startsWith('auth/')) {
        setError(err.message);
      } else {
        setError(getFriendlyErrorMessage(err.code));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left side - Branding */}
        <Box
          sx={{
            flex: 1,
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            position: 'relative',
          }}
        >
          <Box sx={{ textAlign: 'center', color: 'white', p: 4 }}>
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              {t('appName')}
            </Typography>
            <Typography variant="h6">
              {t('home.subtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Right side - Login form */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: { xs: 2, sm: 4 },
            backgroundColor: 'background.default',
          }}
        >
          {/* Language and theme switcher */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 'auto', gap: 1 }}>
            <Select
              value={i18n.language}
              onChange={handleLanguageChange}
              size="small"
              sx={{ minWidth: 80, fontSize: '0.875rem' }}
            >
              <MenuItem value="en">EN</MenuItem>
              <MenuItem value="pl">PL</MenuItem>
            </Select>
            <Tooltip title={themeMode === 'dark' ? 'Light mode' : 'Dark mode'}>
              <IconButton onClick={toggleTheme} color="primary" size="small">
                {themeMode === 'dark' ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Login form */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flex: 1, 
            maxWidth: 450, 
            width: '100%', 
            mx: 'auto' 
          }}>
            <Card elevation={3}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" component="h1" gutterBottom textAlign="center">
                  {t('login')}
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleLogin}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label={t('emailAddress')}
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label={t('password')}
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{ mt: 3, mb: 2 }}
                  >
                    {loading ? t('common.loading') : t('login')}
                  </Button>
                  <Grid container>
                    <Grid item xs>
                      <MuiLink href="#" variant="body2">
                        {t('forgotPassword')}
                      </MuiLink>
                    </Grid>
                    <Grid item>
                      <MuiLink href="/signup" variant="body2">
                        {t('dontHaveAccount')}
                      </MuiLink>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ mb: 'auto' }} />
        </Box>
      </Box>
    </Box>
  );
};


export default Login;
